import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  Chip,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { getDepartmentsByFacilityId } from '../../../../utils/apiClient';

const AssignmentOption = ({ themeMode, currentTheme, value, selectedValue, onChange, title, description, children }) => {
  const UncheckedIcon = (
    <Box sx={{ width: 20, height: 20, borderRadius: '4px', border: `1px solid ${currentTheme.border.primary}` }} />
  );

  const CheckedIcon = (
    <Box sx={{
      width: 20,
      height: 20,
      borderRadius: '4px',
      backgroundColor: currentTheme.common.danger,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />
    </Box>
  );
  
  return (
    <Paper
      elevation={0}
      onClick={() => onChange({ target: { value } })}
      sx={{
        p: 2,
        border: selectedValue === value ? `2px solid ${currentTheme.common.danger}` : `1px solid ${currentTheme.border.primary}`,
        borderRadius: '12px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        transition: 'border-color 0.3s',
        '&:hover': {
          borderColor: currentTheme.common.danger,
        }
      }}
    >
      <FormControlLabel
        value={value}
        control={<Radio icon={UncheckedIcon} checkedIcon={CheckedIcon} />}
        label={
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: currentTheme.text.primary }}>{title}</Typography>
            <Typography variant="body2" sx={{ color: currentTheme.text.secondary }}>{description}</Typography>
          </Box>
        }
        sx={{ width: '100%' }}
      />
      {selectedValue === value && children}
    </Paper>
  );
};

const StepTwo = ({
  currentTheme,
  themeMode,
  setStep,
  assignmentType,
  setAssignmentType,
  facilities,
  loadingFacilities,
  facilitiesError,
  selectedFacility,
  setSelectedFacility,
  selectedDepartment,
  setSelectedDepartment,
  handleContinue,
}) => {
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [departmentsError, setDepartmentsError] = useState(null);

  useEffect(() => {
    if ((assignmentType === 'department' || assignmentType === 'facility') && selectedFacility) {
      const fetchDepartments = async () => {
        setLoadingDepartments(true);
        setDepartmentsError(null);
        setDepartments([]);
        if (assignmentType === 'department') {
            setSelectedDepartment('');
        }
        try {
          const response = await getDepartmentsByFacilityId(selectedFacility);
          // Filter to only show active departments
          const allDepartments = response.response || [];
          const activeDepartments = allDepartments.filter(dept => dept.isActive !== false);
          setDepartments(activeDepartments);
        } catch (err) {
          setDepartmentsError('Failed to fetch departments for this facility.');
        } finally {
          setLoadingDepartments(false);
        }
      };
      fetchDepartments();
    }
  }, [assignmentType, selectedFacility, setSelectedDepartment]);

  const isContinueDisabled = () => {
    if (assignmentType === 'facility') {
      return !selectedFacility;
    }
    if (assignmentType === 'department') {
      return !selectedFacility || !selectedDepartment;
    }
    return false;
  };

  const handleAssignmentTypeChange = (event) => {
    setAssignmentType(event.target.value);
    setSelectedFacility('');
    setSelectedDepartment('');
    setDepartments([]);
    setDepartmentsError(null);
  };

  const handleFacilityChange = (event) => {
    setSelectedFacility(event.target.value);
    setSelectedDepartment('');
  };

  const menuPropsStyles = {
    PaperProps: {
      sx: {
        backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
        "& .MuiMenuItem-root": {
          color: themeMode === 'dark' ? "#fff" : "#000",
          "&:hover": {
            backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
          },
          "&.Mui-selected": {
            backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
            "&:hover": {
              backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            },
          },
        },
      },
    },
  };

  const selectSxStyles = {
    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: themeMode === 'dark' ? "#302d4c" : "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
    },
    "& .MuiSelect-select": {
      color: themeMode === 'dark' ? "#fff" : "#000",
    },
    "& .MuiSelect-icon": {
      color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: 700 }}>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 2 }}>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ color: currentTheme.text.primary, mb: 1 }}>
          Assign to Facility & Departments
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ color: currentTheme.text.secondary, mb: 3 }}>
          Choose how you want to assign this fax number
        </Typography>

        <RadioGroup
          value={assignmentType}
          onChange={handleAssignmentTypeChange}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <AssignmentOption
            themeMode={themeMode}
            currentTheme={currentTheme}
            value="all"
            selectedValue={assignmentType}
            onChange={handleAssignmentTypeChange}
            title="Assign to all facilities and departments"
            description="This number will be available across your entire organization"
          />

          <AssignmentOption
            themeMode={themeMode}
            currentTheme={currentTheme}
            value="facility"
            selectedValue={assignmentType}
            onChange={handleAssignmentTypeChange}
            title="Assign to a facility"
            description="Select a specific facility and all its departments"
          >
            <Box sx={{ mt: 2, pl: '42px' }} onClick={(e) => e.stopPropagation()}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ color: currentTheme.text.secondary, mb: 1, fontWeight: 500 }}>Select Facility</Typography>
                {loadingFacilities ? <CircularProgress size={24} /> : facilitiesError ? <Alert severity="error">{facilitiesError}</Alert> :
                  <Select
                    size="small"
                    value={selectedFacility}
                    onChange={handleFacilityChange}
                    displayEmpty
                    MenuProps={menuPropsStyles}
                    sx={selectSxStyles}
                  >
                    <MenuItem value="" disabled>Choose a facility...</MenuItem>
                    {facilities.map(facility => (
                      <MenuItem key={facility._id} value={facility._id}>
                        {facility.facilityName} ({facility.departments ? facility.departments.length : 0} Departments)
                      </MenuItem>
                    ))}
                  </Select>
                }
              </FormControl>
              {selectedFacility && (
                <Box sx={{ mt: 2 }}>
                  {loadingDepartments ? <CircularProgress size={20} /> : departments.length > 0 &&
                    <Paper elevation={0} sx={{
                        p: 2,
                        backgroundColor: themeMode === 'dark' ? 'rgba(255, 167, 38, 0.1)' : '#FFF4E6',
                        border: `1px solid ${themeMode === 'dark' ? 'rgba(255, 167, 38, 0.2)' : '#FFE8CC'}`,
                        borderRadius: '8px'
                    }}>
                      <Typography variant="body2" sx={{ color: currentTheme.text.secondary, mb: 1 }}>All {departments.length} departments will be included</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {departments.map(dept => (
                            <Chip
                                key={dept._id}
                                label={dept.name}
                                variant="outlined"
                                sx={{
                                    backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                                    borderColor: currentTheme.border.primary,
                                    color: currentTheme.text.primary,
                                    borderRadius: '6px',
                                }}
                            />
                        ))}
                      </Box>
                    </Paper>
                  }
                </Box>
              )}
            </Box>
          </AssignmentOption>

          <AssignmentOption
            themeMode={themeMode}
            currentTheme={currentTheme}
            value="department"
            selectedValue={assignmentType}
            onChange={handleAssignmentTypeChange}
            title="Assign to a department"
            description="Select a specific facility and department"
          >
            <Box sx={{ mt: 2, pl: '42px', display: 'flex', flexDirection: 'column', gap: 2 }} onClick={(e) => e.stopPropagation()}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ color: currentTheme.text.secondary, mb: 1, fontWeight: 500 }}>Select Facility</Typography>
                {loadingFacilities ? <CircularProgress size={24} /> : facilitiesError ? <Alert severity="error">{facilitiesError}</Alert> :
                  <Select
                    size="small"
                    value={selectedFacility}
                    onChange={handleFacilityChange}
                    displayEmpty
                    MenuProps={menuPropsStyles}
                    sx={selectSxStyles}
                  >
                    <MenuItem value="" disabled>Choose a facility...</MenuItem>
                    {facilities.map(facility => (
                      <MenuItem key={facility._id} value={facility._id}>
                        {facility.facilityName} ({facility.departments ? facility.departments.length : 0} Departments)
                      </MenuItem>
                    ))}
                  </Select>
                }
              </FormControl>
              {selectedFacility && (
                <FormControl fullWidth>
                  <Typography variant="body2" sx={{ color: currentTheme.text.secondary, mb: 1, fontWeight: 500 }}>Select Department</Typography>
                  {loadingDepartments ? <CircularProgress size={24} /> : departmentsError ? <Alert severity="error">{departmentsError}</Alert> :
                    <Select
                        size="small"
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        displayEmpty
                        disabled={departments.length === 0}
                        MenuProps={menuPropsStyles}
                        sx={selectSxStyles}
                    >
                      <MenuItem value="" disabled>Choose a department...</MenuItem>
                      {departments.map(dept => (
                        <MenuItem key={dept._id} value={dept._id}>{dept.name}</MenuItem>
                      ))}
                    </Select>
                  }
                </FormControl>
              )}
            </Box>
          </AssignmentOption>
        </RadioGroup>
      </Box>

      <Box display="flex" justifyContent="start" mt={4} pt={2} gap={2} sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          onClick={() => setStep(1)}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: currentTheme.border.primary,
            color: currentTheme.text.primary,
            '&:hover': {
              backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: currentTheme.border.primary,
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={isContinueDisabled()}
          sx={{
            backgroundColor: currentTheme.common.danger,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#E55A3D',
            },
            '&.Mui-disabled': {
              backgroundColor: themeMode === 'dark' ? 'rgba(255, 107, 71, 0.2)' : 'rgba(0, 0, 0, 0.12)',
              color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
            },
            textTransform: 'none',
            borderRadius: '8px',
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default StepTwo;
